import Tooltip from '@mui/material/Tooltip'
import { useEffect, useState } from 'react'

import { COP_API_BASE_URL, getCaseFileUrl } from '../../../constants/service-endpoints'
import { getBlob } from '../../../services/http.service'
import {
  DEFAULT_COL_ATTACHMENTS,
  DEFAULT_COL_COMMENTS,
  DEFAULT_COL_DATE,
  DEFAULT_COL_LAST_MODIFIED_BY,
  DEFAULT_COL_VIEW_DOWNLOAD,
  DEFAULT_FILE_DOWNLOAD_ERROR_LABEL,
  DEFAULT_ITEMS_PER_PAGE_LABEL,
  DEFAULT_LOADING_LABEL,
  DEFAULT_NO_COMMENTS_LABEL,
  DEFAULT_NUMBER_OF_COMMENTS_LABEL,
  DEFAULT_VIEW_LABEL,
} from '../constants/labels'
import { CaseComment, fetchCaseComments } from '../services/CopCaseDetails.service'
import { formatLondonTooltip } from '../utils/londonTooltip'

const ITEMS_PER_PAGE_OPTIONS = [10, 20]

const DateCellTooltip = ({ value, tooltip }: { value: string; tooltip?: string }) => {
  if (!tooltip || !value) return <>{value}</>

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: '#1a202c',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 1px 6px rgb(0 0 0 / 30%)',
            whiteSpace: 'nowrap',
            maxWidth: 'none',
          },
        },
        arrow: { sx: { color: '#fff' } },
      }}
    >
      <span>{value}</span>
    </Tooltip>
  )
}

const MIME_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  mp4: 'video/mp4',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  zip: 'application/zip',
}

export type CaseCommentsFilesTabProps = {
  caseId?: string
  refreshKey?: number
  colLastModifiedBy?: string
  colDate?: string
  colComments?: string
  colAttachments?: string
  colViewDownload?: string
  viewLabel?: string
  noCommentsLabel?: string
  numberOfCommentsLabel?: string
  itemsPerPageLabel?: string
  loadingLabel?: string
  fileDownloadErrorLabel?: string
}

export default function CaseCommentsFilesTab({
  caseId,
  refreshKey = 0,
  colLastModifiedBy = DEFAULT_COL_LAST_MODIFIED_BY,
  colDate = DEFAULT_COL_DATE,
  colComments = DEFAULT_COL_COMMENTS,
  colAttachments = DEFAULT_COL_ATTACHMENTS,
  colViewDownload = DEFAULT_COL_VIEW_DOWNLOAD,
  viewLabel = DEFAULT_VIEW_LABEL,
  noCommentsLabel = DEFAULT_NO_COMMENTS_LABEL,
  numberOfCommentsLabel = DEFAULT_NUMBER_OF_COMMENTS_LABEL,
  itemsPerPageLabel = DEFAULT_ITEMS_PER_PAGE_LABEL,
  loadingLabel = DEFAULT_LOADING_LABEL,
  fileDownloadErrorLabel = DEFAULT_FILE_DOWNLOAD_ERROR_LABEL,
}: Readonly<CaseCommentsFilesTabProps>) {
  const [comments, setComments] = useState<CaseComment[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0])
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null)
  const [fileErrorId, setFileErrorId] = useState<string | null>(null)

  useEffect(() => {
    if (!caseId) {
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    fetchCaseComments(caseId)
      .then(response => {
        if (cancelled) return
        setComments(response?.value ?? [])
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [caseId, refreshKey])

  const totalItems = comments.length
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const pageItems = comments.slice(startIndex, startIndex + itemsPerPage)

  const handleFileDownload = async ({
    comment,
    fileKey,
    fileName,
    fileUrl,
  }: {
    comment: CaseComment
    fileKey: string
    fileName: string
    fileUrl: string
  }) => {
    setLoadingFileId(fileKey)
    setFileErrorId(null)
    try {
      const rawBlob = await getBlob(fileUrl)
      const ext = (comment.attachment?.fileExtension ?? '').toLowerCase()
      const mimeType = MIME_TYPES[ext] ?? rawBlob.type ?? 'application/octet-stream'
      const blob = rawBlob.type && rawBlob.type !== 'application/octet-stream'
        ? rawBlob
        : new Blob([rawBlob], { type: mimeType })
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, '_blank')
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
    } catch {
      setFileErrorId(fileKey)
    } finally {
      setLoadingFileId(null)
    }
  }

  const renderCommentRow = (comment: CaseComment, index: number) => {
    const attachment = comment.attachment
    const hasAttachment = attachment?.name && attachment?.fileExtension
    const fileName = hasAttachment
      ? `${attachment.name}.${attachment.fileExtension}`
      : ''
    const fileUrl = fileName && caseId
      ? `${COP_API_BASE_URL}${getCaseFileUrl(caseId, fileName)}`
      : ''
    const fileKey = comment.id ?? String(index)
    const isLoading = loadingFileId === fileKey
    const hasError = fileErrorId === fileKey

    return (
      <tr key={comment.id ?? index}>
        <td>{comment.user ?? ''}</td>
        <td>
          <DateCellTooltip value={comment.date ?? ''} tooltip={formatLondonTooltip(comment.date)} />
        </td>
        <td className="cop-comments-table__comment-cell">
          <span className="cop-comments-table__comment-text">{comment.details ?? ''}</span>
        </td>
        <td>{hasAttachment ? fileName : ''}</td>
        <td>
          {hasAttachment && caseId && fileName && fileUrl ? (
            <>
              <button
                type="button"
                className={`cop-comments-table__btn${isLoading ? ' cop-comments-table__btn--disabled' : ''}`}
                disabled={isLoading}
                onClick={() => handleFileDownload({ comment, fileKey, fileName, fileUrl })}
              >
                {isLoading ? '…' : viewLabel}
              </button>
              {hasError && (
                <p className="cop-comments-table__file-error">{fileDownloadErrorLabel}</p>
              )}
            </>
          ) : null}
        </td>
      </tr>
    )
  }

  return (
    <div className="cop-case-details__panel cop-comments-table">
      <table className="cop-comments-table__table">
        <thead>
          <tr>
            <th>{colLastModifiedBy}</th>
            <th>{colDate}</th>
            <th>{colComments}</th>
            <th>{colAttachments}</th>
            <th>{colViewDownload}</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={5} className="cop-comments-table__empty">{loadingLabel}</td>
            </tr>
          )}
          {!loading && pageItems.length === 0 && (
            <tr>
              <td colSpan={5} className="cop-comments-table__empty">{noCommentsLabel}</td>
            </tr>
          )}
          {!loading && pageItems.length > 0 && (
            pageItems.map((comment, index) => renderCommentRow(comment, index))
          )}
        </tbody>
      </table>

      <div className="cop-comments-table__footer">
        <div className="cop-comments-table__footer-left">
          <span className="cop-comments-table__count">
            {numberOfCommentsLabel} {String(totalItems).padStart(2, '0')}
          </span>
        </div>
        <div className="cop-comments-table__pagination">
          <span>{itemsPerPageLabel}</span>
          <select
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value))
              setCurrentPage(1)
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <button
            type="button"
            className="cop-comments-table__nav-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            &#8249;
          </button>
          <span>{currentPage}/{totalPages}</span>
          <button
            type="button"
            className="cop-comments-table__nav-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            &#8250;
          </button>
        </div>
      </div>
    </div>
  )
}
